import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import "./UnscrambleSentencesByListening.scss";
import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import { useDrag, useDrop } from "react-dnd";
import { BsSoundwave } from "react-icons/bs";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ButtonGroup, ButtonToolbar, IconButton } from "rsuite";
import { LanguageContext } from "../../../app/context/LanguageContext";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { Stepper } from "../../../shared/components/Stepper/Stepper";
import { playSynthesisUtterance, shuffle } from "../../../shared/helpers";

type UnscrambleSentencesByListeningType = {
	data: string[];
	withListening?: boolean;
	activityId: string;
};

function DraggableItem({
	item,
	index,
	moveItem,
}: {
	item: string;
	index: number;
	moveItem: (fromIndex: number, toIndex: number) => void;
}) {
	const ref = useRef(null);
	const [, drop] = useDrop({
		accept: "WORD",
		hover: (draggedItem: { index: number }) => {
			if (draggedItem.index !== index) {
				moveItem(draggedItem.index, index);
				draggedItem.index = index;
			}
		},
	});
	const [, drag] = useDrag({
		type: "WORD",
		item: { index },
	});
	drag(drop(ref));

	return (
		<div ref={ref} className="word-item">
			{item}
		</div>
	);
}

const UnscrambleSentencesByListening: React.FC<
	UnscrambleSentencesByListeningType
> = ({ data, withListening = true, activityId }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const sentence = data[currentIndex];
	const [isSpeaking, setIsSpeaking] = useState(false);
	const [currentWords, setCurrentWords] = useState<string[]>([]);
	const { triggerActivityCompletion } = useActivityCompletion();
	const prefixClass = "unscramble-container";
	const { language } = useContext(LanguageContext);
	console.log(language);
	const [steps, setSteps] = useState(
		data.map((_, index) => {
			return {
				index,
				current: index === currentIndex,
				completed: false,
			};
		}),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setSteps(
			steps.map((steps, index) => {
				return {
					...steps,
					current: index === currentIndex,
				};
			}),
		);
	}, [currentIndex]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const sentence = data[currentIndex];
		const words = sentence?.split(" ");
		setCurrentWords(shuffle(words));
	}, [currentIndex]);

	const moveItem = (fromIndex: number, toIndex: number) => {
		const updatedWords = [...currentWords];
		const [movedItem] = updatedWords.splice(fromIndex, 1);
		updatedWords.splice(toIndex, 0, movedItem);
		setCurrentWords(updatedWords);
	};

	const resetActivity = () => {
		setCurrentIndex(0);
		setSteps(
			data.map((_, index) => {
				return {
					index,
					current: index === currentIndex,
					completed: false,
				};
			}),
		);
	};

	const nextSentenceHandler = () => {
		if (currentIndex < data?.length - 1) {
			setCurrentIndex((prev) => prev + 1);
		}
	};

	const prevSentenceHandler = () => {
		if (currentIndex > 0 && !steps[currentIndex - 1]?.completed) {
			setCurrentIndex((prev) => prev - 1);
		}
	};

	const checkAnswersHandler = async () => {
		if (currentWords.join(" ") === sentence) {
			setSteps((prev) => {
				const newSteps = [...prev];
				newSteps[currentIndex].completed = true;
				return newSteps;
			});

			if (currentIndex < data.length - 1) {
				setTimeout(() => {
					setCurrentIndex((prev) => prev + 1);
				}, 1500);
			} else {
				setTimeout(async () => {
					setFireworksTrigger(true);
					await triggerActivityCompletion(activityId, 16, resetActivity);
					console.log("All sentences completed!");
				}, 2000);
			}
		} else {
			alert("Some answers are incorrect. Try again!");
		}
	};

	return (
		<div className={prefixClass}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			<div className={`${prefixClass}__toolbar`}>
				<div>
					<ButtonToolbar>
						<IconButton
							onClick={resetActivity}
							appearance="primary"
							icon={<ReloadIcon color="white" />}
						/>
						<IconButton
							onClick={checkAnswersHandler}
							appearance="primary"
							icon={<CheckOutlineIcon color="white" />}
						>
							Check
						</IconButton>
						{withListening && (
							<IconButton
								disabled={isSpeaking}
								appearance="primary"
								icon={<BsSoundwave />}
								onClick={() => {
									playSynthesisUtterance({
										word: sentence,
										onStart: () => setIsSpeaking(true),
										onEnd: () => setIsSpeaking(false),
										language,
									});
								}}
							/>
						)}
						<ButtonGroup>
							<IconButton
								onClick={prevSentenceHandler}
								appearance="primary"
								disabled={steps[currentIndex - 1]?.completed}
								icon={<IoIosArrowBack />}
							/>
							<IconButton
								onClick={nextSentenceHandler}
								appearance="primary"
								icon={<IoIosArrowForward />}
							/>
						</ButtonGroup>
					</ButtonToolbar>
				</div>
			</div>
			<Stepper steps={steps} />
			<div className={`${prefixClass}__inner-container`}>
				<div className="words-container">
					{currentWords.map((word, index) => (
						<DraggableItem
							key={word}
							index={index}
							item={word}
							moveItem={moveItem}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default UnscrambleSentencesByListening;
